import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi } from '../lib/api';
import { OPTIONS, CATEGORIES, formatPrice, formatDate } from '../lib/utils';
import { ArrowLeft, ArrowRight, Check, Users, Utensils, Calendar, Settings, CreditCard, Loader2, AlertTriangle } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { fr } from 'date-fns/locale';
import { track } from '../lib/analytics';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Logo from '../components/Logo';

// Retourne la date civile locale (YYYY-MM-DD) sans conversion UTC, pour éviter tout décalage de jour.
const toLocalDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const STEPS = [
  { id: 'foyer', label: 'Foyer', icon: Users },
  { id: 'options', label: 'Options', icon: Settings },
  { id: 'creneau', label: 'Créneau', icon: Calendar },  // Moved before menus for season logic
  { id: 'menus', label: 'Menus', icon: Utensils },
  { id: 'equipements', label: 'Équipements', icon: Settings },
  { id: 'recap', label: 'Récapitulatif', icon: CreditCard }
];

export default function ReservationPage() {
  const { user, updateProfile } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [sectorCheck, setSectorCheck] = useState(null);
  const [pricing, setPricing] = useState({ formulas: [], options: [] });
  const [promo, setPromo] = useState(null);
  const loadPromo = () => clientApi.getPromoEligibility().then((r) => setPromo(r.data)).catch(() => setPromo(null));
  
  // Season state for menu automation
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasonError, setSeasonError] = useState(null);
  const [loadingMenus, setLoadingMenus] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Foyer
    household_size: user?.household_size || 2,
    address: user?.address || '',
    postal_code: user?.postal_code || '',
    city: user?.city || '',
    phone: user?.phone || '',
    
    // Options
    options: {
      lunch: false,
      dessert: false,
      patisserie: false
    },
    selected_options_input: [],
    
    // Menus - Dynamic selections by category
    menu_id: null,
    category_selections: {}, // { category_id: [recipe_id, recipe_id, ...] }
    no_fish: false, // « Je ne souhaite pas de poisson »
    fish_replacement: null, // { recipe_id, category_id } — plat mijoté/four de remplacement
    
    // Legacy - kept for backward compatibility
    selected_recipes: {
      bloc1_mijote: null,
      bloc1_four: null,
      bloc1_poisson: null,
      bloc2_soupe1: null,
      bloc2_soupe2: null,
      bloc3_tarte1: null,
      bloc3_tarte2: null,
      bloc4_sauce: null,
      bloc5_pates: null,
      bloc5_gratin: null
    },
    
    // Créneau - New system
    date: null,
    slot_type: 'matin', // matin (08:00-12:00) or apres_midi (13:30-17:30)
    preferred_time: '', // Indicative preferred time within slot
    slot_id: null,
    employee_id: null, // Bound when a recommended slot is chosen (null = manual half-day)
    start_time: null,  // HH:MM of the chosen recommended slot
    end_time: null,    // HH:MM (display only)
    
    // Équipements
    equipment: {
      four: true,
      plaques: true,
      frigo: true,
      evier: true,
      mixeur: false,
      casseroles: false,
      poele: false,
      plat: false,
      faitout: false
    },
    
    // URSSAF
    has_urssaf_account: false,
    urssaf_advance_enabled: false,
    
    // Notes
    notes: ''
  });

  // Get formula from household size using the SINGLE source of truth: Admin → Tarifs
  // (/client/pricing). No hardcoded price fallback, no legacy 3-formula list.
  const getFormulaInfo = () => {
    const size = formData.household_size;
    const formula = pricing.formulas.find(f => size >= f.min_persons && size <= f.max_persons);
    if (formula) {
      return { key: formula.key, ...formula };
    }
    // Aucun fallback de prix hardcodé : si aucune formule tarifaire ne couvre ce foyer,
    // on ne fabrique pas un prix arbitraire.
    return { key: null, name: '—', description: '', price: 0 };
  };

  const formula = getFormulaInfo().key;
  const formulaInfo = getFormulaInfo();

  // Get options as a map for easy access
  const optionsMap = pricing.options.reduce((acc, opt) => {
    acc[opt.key] = opt;
    return acc;
  }, {});

  useEffect(() => {
    loadInitialData();
  }, []);

  // GA4 : début du parcours de réservation + abandon (mesuré au démontage si non finalisé).
  const completedRef = useRef(false);
  const stepRef = useRef(0);
  useEffect(() => { stepRef.current = currentStep; }, [currentStep]);
  useEffect(() => {
    track('reservation_start');
    return () => {
      if (!completedRef.current) track('reservation_abandon', { last_step: stepRef.current });
    };
  }, []);

  useEffect(() => {
    if (formData.postal_code?.length === 5) {
      checkSector(formData.postal_code);
    }
  }, [formData.postal_code]);

  useEffect(() => {
    if (formData.date && sectorCheck?.in_sector) {
      loadAvailableSlots();
      // Load menus based on the service date for season filtering
      loadMenusForDate();
    }
  }, [formData.date, sectorCheck]);

  const loadInitialData = async () => {
    try {
      const [recipesRes, pricingRes] = await Promise.all([
        clientApi.getRecipes(),
        clientApi.getPricing()
      ]);
      setRecipes(recipesRes.data);
      setPricing(pricingRes.data);
      loadPromo();
      // Note: Menus are now loaded separately when a date is selected
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  // Load menus filtered by the season active on the selected service date
  const loadMenusForDate = async () => {
    if (!formData.date) return;
    
    setLoadingMenus(true);
    setSeasonError(null);
    setActiveSeason(null);
    
    try {
      const dateStr = toLocalDateStr(formData.date);
      const response = await clientApi.getMenusForDate(dateStr);
      
      if (!response.data.has_season) {
        // No season available for this date
        setSeasonError(response.data.message || 'Aucun menu disponible pour cette date');
        setMenus([]);
        setSelectedMenu(null);
        setRecipes([]);
      } else {
        // Season found - set menus and recipes
        setActiveSeason(response.data.season);
        setMenus(response.data.menus || []);
        
        // Auto-select first menu if available
        if (response.data.menus?.length > 0) {
          const firstMenu = response.data.menus[0];
          setSelectedMenu(firstMenu);
          setFormData(prev => ({ ...prev, menu_id: firstMenu.menu_id }));
        } else {
          setSelectedMenu(null);
          setFormData(prev => ({ ...prev, menu_id: null }));
        }
        
        // Also load recipes for this season
        const recipesRes = await clientApi.getRecipesForDate(dateStr);
        if (recipesRes.data.has_season) {
          setRecipes(recipesRes.data.recipes || []);
        }
      }
    } catch (error) {
      console.error('Error loading menus for date:', error);
      setSeasonError('Erreur lors du chargement des menus');
    } finally {
      setLoadingMenus(false);
    }
  };

  const checkSector = async (postalCode) => {
    try {
      const response = await clientApi.checkSector(postalCode);
      setSectorCheck(response.data);
      if (!response.data.in_sector) {
        track('sector_unavailable', { departement: String(postalCode).slice(0, 2) });
        toast.info('Votre secteur n\'est pas encore desservi. Nous avons enregistré votre demande.');
      }
    } catch (error) {
      console.error('Error checking sector:', error);
    }
  };

  const loadAvailableSlots = async () => {
    try {
      const dateStr = toLocalDateStr(formData.date);
      const response = await clientApi.getAvailableSlots(formData.postal_code, dateStr);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Error loading slots:', error);
    }
  };

  const calculateTotal = () => {
    let total = formulaInfo.price;
    Object.entries(formData.options).forEach(([key, value]) => {
      if (value && optionsMap[key] && !optionsMap[key].has_formats) {
        total += optionsMap[key].price;
      }
    });
    // Options structurées : prix = prix du format choisi
    (formData.selected_options_input || []).forEach((so) => {
      const opt = optionsMap[so.option_key];
      const fmt = opt?.formats?.find((f) => f.format_id === so.format_id);
      if (fmt) total += fmt.price;
    });
    // Supplément déplacement (zone géographique, source = secteur du code postal)
    total += Number(sectorCheck?.sector?.travel_surcharge || 0);
    return total;
  };

  const handleNext = async () => {
    // Validate current step
    if (currentStep === 0) {
      if (!formData.postal_code || formData.postal_code.length !== 5) {
        toast.error('Veuillez entrer un code postal valide');
        return;
      }
      if (!formData.address || !formData.city) {
        toast.error('Veuillez compléter votre adresse');
        return;
      }
      // Update user profile
      try {
        await updateProfile({
          household_size: formData.household_size,
          address: formData.address,
          postal_code: formData.postal_code,
          city: formData.city,
          phone: formData.phone
        });
      } catch (error) {
        console.error('Error updating profile:', error);
      }
    }
    
    // Step 2 is now Créneau (date selection)
    if (currentStep === 2 && !formData.date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    // Un créneau proposé par le moteur doit être réellement sélectionné (plus de faux créneau)
    if (currentStep === 2 && !formData.slot_id) {
      toast.error('Veuillez sélectionner un créneau proposé.');
      return;
    }
    
    // Check for season error before going to menus step
    if (currentStep === 2 && seasonError) {
      toast.error(seasonError);
      return;
    }
    
    // Step 3 is now Menus
    if (currentStep === 3) {
      // Validate menus - check category selections
      if (selectedMenu && selectedMenu.categories) {
        const cats = selectedMenu.categories;
        const fishSlots = cats
          .filter(c => c.category_type === 'fish')
          .reduce((s, c) => s + (c.max_choices || 1), 0);
        if (formData.no_fish) {
          if (fishSlots === 0) {
            toast.error("Ce menu ne comporte pas de poisson.");
            return;
          }
          // Base choices required for every non-fish category
          for (const category of cats) {
            if (category.category_type === 'fish') continue;
            const selections = formData.category_selections[category.category_id] || [];
            if (selections.length < category.max_choices) {
              toast.error(`Veuillez sélectionner ${category.max_choices} plat(s) pour "${category.name}"`);
              return;
            }
          }
          // Exactly one simmered/oven replacement for the fish
          if (!formData.fish_replacement) {
            toast.error('Veuillez choisir un plat mijoté ou au four en remplacement du poisson.');
            return;
          }
        } else {
          for (const category of cats) {
            const selections = formData.category_selections[category.category_id] || [];
            if (selections.length < category.max_choices) {
              toast.error(`Veuillez sélectionner ${category.max_choices} plat(s) pour "${category.name}"`);
              return;
            }
          }
        }
      } else {
        // Fallback to legacy validation
        const requiredRecipes = ['bloc1_mijote', 'bloc1_four', 'bloc1_poisson', 'bloc2_soupe1', 'bloc2_soupe2', 'bloc3_tarte1', 'bloc3_tarte2', 'bloc4_sauce'];
        const missingRecipes = requiredRecipes.filter(key => !formData.selected_recipes[key]);
        if (missingRecipes.length > 0) {
          toast.error('Veuillez sélectionner tous les plats requis');
          return;
        }
      }
      track('select_recipes', { menu_id: formData.menu_id || null });
    }
    
    // Step 4 is Équipements
    if (currentStep === 4) {
      const requiredEquipment = ['four', 'plaques', 'frigo', 'evier'];
      const missingEquipment = requiredEquipment.filter(key => !formData.equipment[key]);
      if (missingEquipment.length > 0) {
        toast.error('Certains équipements obligatoires manquent');
        return;
      }
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build selected_recipe_ids from category selections
      let category_selections = { ...formData.category_selections };
      // Apply fish replacement: remove any fish-category selection, add the extra simmered/oven dish
      if (formData.no_fish && selectedMenu?.categories) {
        selectedMenu.categories
          .filter(c => c.category_type === 'fish')
          .forEach(c => { category_selections[c.category_id] = []; });
        if (formData.fish_replacement) {
          const { recipe_id, category_id } = formData.fish_replacement;
          const existing = category_selections[category_id] || [];
          if (!existing.includes(recipe_id)) {
            category_selections[category_id] = [...existing, recipe_id];
          }
        }
      }
      let selected_recipe_ids = [];
      if (selectedMenu && Object.keys(category_selections).length > 0) {
        Object.values(category_selections).forEach(recipeIds => {
          selected_recipe_ids = [...selected_recipe_ids, ...recipeIds];
        });
      } else {
        // Legacy: extract from selected_recipes
        selected_recipe_ids = Object.values(formData.selected_recipes).filter(Boolean);
      }
      
      const reservationData = {
        household_size: formData.household_size,
        formula: formula,
        options: formData.options,
        selected_options_input: formData.selected_options_input || [],
        selected_recipes: formData.selected_recipes,
        selected_recipe_ids: selected_recipe_ids,
        menu_id: formData.menu_id,
        category_selections: category_selections,
        no_fish: formData.no_fish,
        equipment: formData.equipment,
        date: toLocalDateStr(formData.date),
        slot_type: formData.slot_type,
        preferred_time: formData.preferred_time || null,
        slot_id: formData.slot_id,
        employee_id: formData.employee_id || null,
        start_time: formData.start_time || null,
        has_urssaf_account: formData.has_urssaf_account,
        urssaf_advance_enabled: formData.urssaf_advance_enabled,
        notes: formData.notes,
        promo_expected: !!promo?.eligible
      };
      
      const response = await clientApi.createReservation(reservationData);
      completedRef.current = true;
      track('reservation_created', {
        transaction_id: response.data.reservation_id,
        value: calculateTotal(),
        currency: 'EUR',
        formula: formula,
        household_size: formData.household_size,
      });
      toast.success('Réservation créée avec succès !');
      navigate(`/client/reservations/${response.data.reservation_id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      track('reservation_error', { message: String(detail || 'error').slice(0, 100) });
      toast.error(detail || 'Erreur lors de la création de la réservation');
      if (error.response?.status === 409 && promo?.eligible) loadPromo();
      console.error('Error creating reservation:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRecipesByCategory = (category) => {
    return recipes.filter(r => r.category === category);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <FoyerStep formData={formData} setFormData={setFormData} formula={formula} formulaInfo={formulaInfo} />;
      case 1:
        return <OptionsStep formData={formData} setFormData={setFormData} optionsMap={optionsMap} pricingOptions={pricing.options} />;
      case 2:
        return <CreneauStep formData={formData} setFormData={setFormData} availableSlots={availableSlots} sectorCheck={sectorCheck} formulaKey={formula} />;
      case 3:
        return (
          <MenusStepCategorized 
            formData={formData} 
            setFormData={setFormData} 
            menus={menus}
            selectedMenu={selectedMenu}
            setSelectedMenu={setSelectedMenu}
            recipes={recipes}
            getRecipesByCategory={getRecipesByCategory}
            activeSeason={activeSeason}
            seasonError={seasonError}
            loadingMenus={loadingMenus}
          />
        );
      case 4:
        return <EquipementsStep formData={formData} setFormData={setFormData} />;
      case 5:
        return <RecapStep formData={formData} setFormData={setFormData} formula={formula} formulaInfo={formulaInfo} calculateTotal={calculateTotal} recipes={recipes} optionsMap={optionsMap} selectedMenu={selectedMenu} sectorCheck={sectorCheck} promo={promo} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/client" className="flex items-center gap-2 text-[#5C6A61] hover:text-[#2A312C]">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Retour</span>
            </Link>
            <Logo />
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    index < currentStep 
                      ? 'bg-[#A5BBA8] text-white' 
                      : index === currentStep 
                        ? 'bg-[#C86B51] text-white' 
                        : 'bg-[#E3DED5] text-[#5C6A61]'
                  }`}
                >
                  {index < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${index < currentStep ? 'bg-[#A5BBA8]' : 'bg-[#E3DED5]'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-[#5C6A61]">
            Étape {currentStep + 1}/{STEPS.length} - {STEPS[currentStep].label}
          </p>
        </div>

        {/* Step content */}
        <div className="card-default mb-8" data-testid={`step-${STEPS[currentStep].id}`}>
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Précédent
          </button>
          
          {currentStep === STEPS.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-secondary"
              data-testid="submit-reservation-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              Confirmer la réservation
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="btn-primary"
              data-testid="next-step-btn"
            >
              Suivant
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

// Step Components
function FoyerStep({ formData, setFormData, formula, formulaInfo }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Composition de votre foyer
        </h2>
        <p className="text-[#5C6A61]">Indiquez le nombre de personnes pour adapter les quantités</p>
      </div>
      
      <div>
        <Label className="text-[#2A312C] mb-2 block">Nombre de personnes</Label>
        <div className="flex items-center gap-4">
          {[1, 2, 3, 4, 5, 6].map((num) => (
            <button
              key={num}
              onClick={() => setFormData({ ...formData, household_size: num })}
              className={`w-12 h-12 rounded-xl font-semibold transition-colors ${
                formData.household_size === num
                  ? 'bg-[#C86B51] text-white'
                  : 'bg-white border border-[#E3DED5] text-[#2A312C] hover:border-[#A5BBA8]'
              }`}
              data-testid={`household-size-${num}`}
            >
              {num}
            </button>
          ))}
        </div>
        
        {/* Note sur les portions */}
        <div className="mt-4 p-4 bg-[#F9F6F0] rounded-xl border border-[#E3DED5]">
          <p className="text-sm text-[#2A312C] font-medium mb-1">
            À propos des portions
          </p>
          <p className="text-sm text-[#5C6A61]">
            Nos portions sont prévues pour un appétit <span className="font-medium text-[#2A312C]">classique à généreux</span>.
          </p>
          <p className="text-sm text-[#5C6A61] mt-2">
            💡 <span className="italic">Pour les personnes ayant un très gros appétit, nous recommandons de compter une personne supplémentaire lors de la sélection.</span>
          </p>
        </div>
      </div>
      
      <div className="p-4 bg-[#A5BBA8]/10 rounded-xl">
        <p className="font-semibold text-[#2A312C]">Formule : {formulaInfo.name}</p>
        <p className="text-[#5C6A61]">{formulaInfo.description} - {formatPrice(formulaInfo.price)}</p>
      </div>
      
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[#2A312C] mb-2 block">Adresse</Label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="input-default"
            placeholder="123 rue de la Cuisine"
            data-testid="address-input"
          />
        </div>
        <div>
          <Label className="text-[#2A312C] mb-2 block">Téléphone</Label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="input-default"
            placeholder="06 12 34 56 78"
            data-testid="phone-input"
          />
        </div>
        <div>
          <Label className="text-[#2A312C] mb-2 block">Code postal</Label>
          <input
            type="text"
            value={formData.postal_code}
            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.slice(0, 5) })}
            className="input-default"
            placeholder="75001"
            maxLength={5}
            data-testid="postal-code-input"
          />
        </div>
        <div>
          <Label className="text-[#2A312C] mb-2 block">Ville</Label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            className="input-default"
            placeholder="Paris"
            data-testid="city-input"
          />
        </div>
      </div>
    </div>
  );
}

function OptionsStep({ formData, setFormData, optionsMap, pricingOptions }) {
  const toggleOption = (key) => {
    setFormData({
      ...formData,
      options: { ...formData.options, [key]: !formData.options[key] }
    });
  };

  // Source unique : options réellement actives/configurées dans l'admin (aucun fallback).
  const allOptions = pricingOptions || [];
  // Drive supprimé : jamais affiché. Simple = sans formats ; structuré = has_formats.
  const simpleOptions = allOptions.filter((o) => o.key !== 'drive' && !o.has_formats);
  const structuredOptions = allOptions.filter((o) => o.key !== 'drive' && o.has_formats);

  const setStructured = (list) => setFormData({ ...formData, selected_options_input: list });

  // 0 option active => aucune section Options visible.
  if (simpleOptions.length === 0 && structuredOptions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Options supplémentaires
        </h2>
        <p className="text-[#5C6A61]">Personnalisez votre prestation selon vos besoins</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {simpleOptions.map((option) => (
          <div
            key={option.key}
            onClick={() => toggleOption(option.key)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
              formData.options[option.key]
                ? 'border-[#C86B51] bg-[#C86B51]/5'
                : 'border-[#E3DED5] hover:border-[#A5BBA8]'
            }`}
            data-testid={`option-${option.key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-[#2A312C]">{option.name}</h3>
              <Checkbox checked={!!formData.options[option.key]} />
            </div>
            <p className="text-sm text-[#5C6A61] mb-2">{option.description}</p>
            <p className="text-[#C86B51] font-semibold">+{formatPrice(option.price)}</p>
          </div>
        ))}
      </div>

      {structuredOptions.map((option) => (
        <StructuredOption
          key={option.key}
          option={option}
          value={(formData.selected_options_input || []).find((s) => s.option_key === option.key)}
          onChange={(entry) => {
            const rest = (formData.selected_options_input || []).filter((s) => s.option_key !== option.key);
            setStructured(entry ? [...rest, entry] : rest);
          }}
        />
      ))}
    </div>
  );
}

function StructuredOption({ option, value, onChange }) {
  const formats = (option.formats || []).filter((f) => f.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const selectedFormat = formats.find((f) => f.format_id === value?.format_id) || null;

  const selectFormat = (fmt) => {
    if (!fmt) { onChange(null); return; }
    onChange({ option_key: option.key, format_id: fmt.format_id, selections: [] });
  };

  const getGroupChoices = (groupId) => {
    const g = (value?.selections || []).find((s) => s.group_id === groupId);
    return g?.choices || [];
  };

  const updateGroup = (groupId, choices) => {
    const others = (value?.selections || []).filter((s) => s.group_id !== groupId);
    const selections = choices.length ? [...others, { group_id: groupId, choices }] : others;
    onChange({ option_key: option.key, format_id: selectedFormat.format_id, selections });
  };

  const totalBatches = (value?.selections || []).reduce(
    (sum, g) => sum + g.choices.reduce((s, c) => s + (c.batches || 1), 0), 0
  );

  return (
    <div className="rounded-xl border-2 border-[#E3DED5] p-4" data-testid={`structured-option-${option.key}`}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold text-[#2A312C]">{option.name}</h3>
      </div>
      {option.description && <p className="text-sm text-[#5C6A61] mb-3">{option.description}</p>}

      <div className="flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          onClick={() => selectFormat(null)}
          className={`px-3 py-1.5 rounded-lg text-sm border ${!selectedFormat ? 'bg-[#5C6A61] text-white border-[#5C6A61]' : 'border-[#E3DED5] text-[#5C6A61]'}`}
          data-testid={`format-none-${option.key}`}
        >
          Aucune
        </button>
        {formats.map((f) => (
          <button
            type="button"
            key={f.format_id}
            onClick={() => selectFormat(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${selectedFormat?.format_id === f.format_id ? 'bg-[#C86B51] text-white border-[#C86B51]' : 'border-[#E3DED5] text-[#2A312C] hover:border-[#A5BBA8]'}`}
            data-testid={`format-${option.key}-${f.name}`}
          >
            {f.name} · +{formatPrice(f.price)}
          </button>
        ))}
      </div>

      {selectedFormat && (
        <div className="space-y-4">
          {selectedFormat.included_batches != null && (() => {
            const choiceYield = {};
            (selectedFormat.groups || []).forEach((g) => (g.choices || []).forEach((c) => { if (c.yield_per_batch != null) choiceYield[c.choice_id] = { y: c.yield_per_batch, u: c.yield_unit || 'pots' }; }));
            let totalPots = 0; let unit = 'pots';
            (value?.selections || []).forEach((g) => g.choices.forEach((c) => { const cy = choiceYield[c.choice_id]; if (cy) { totalPots += (c.batches || 1) * cy.y; unit = cy.u; } }));
            const ok = totalBatches === selectedFormat.included_batches;
            return (
              <div className={`text-sm rounded-lg px-3 py-2 ${ok ? 'bg-[#A5BBA8]/15 text-[#2A312C]' : 'bg-[#FBE7E1] text-[#C86B51]'}`} data-testid={`batches-info-${option.key}`}>
                Lots : {totalBatches} / {selectedFormat.included_batches}
                {totalPots > 0 && <span> · {totalPots} {unit}</span>}
                {!ok && <span> — sélectionnez exactement {selectedFormat.included_batches} lot(s)</span>}
              </div>
            );
          })()}
          {(selectedFormat.groups || []).filter((g) => g.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((group) => {
            const isBatchMode = (group.choices || []).some((c) => c.yield_per_batch != null);
            const chosen = getGroupChoices(group.group_id);
            const activeChoices = (group.choices || []).filter((c) => c.is_active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));

            const toggleChoice = (choice) => {
              const exists = chosen.find((c) => c.choice_id === choice.choice_id);
              let next;
              if (exists) next = chosen.filter((c) => c.choice_id !== choice.choice_id);
              else {
                if (group.max_select === 1) next = [{ choice_id: choice.choice_id, batches: 1 }];
                else if (chosen.length >= group.max_select) return;
                else next = [...chosen, { choice_id: choice.choice_id, batches: 1 }];
              }
              updateGroup(group.group_id, next);
            };
            const setBatches = (choice, delta) => {
              const exists = chosen.find((c) => c.choice_id === choice.choice_id);
              let next;
              if (!exists) next = [...chosen, { choice_id: choice.choice_id, batches: 1 }];
              else next = chosen.map((c) => c.choice_id === choice.choice_id ? { ...c, batches: Math.max(1, (c.batches || 1) + delta) } : c);
              if (delta < 0 && exists && (exists.batches || 1) + delta < 1) next = chosen.filter((c) => c.choice_id !== choice.choice_id);
              updateGroup(group.group_id, next);
            };

            return (
              <div key={group.group_id} data-testid={`group-${option.key}-${group.name}`}>
                <p className="text-sm font-medium text-[#2A312C] mb-2">
                  {group.name} <span className="text-xs text-[#5C6A61]">({group.min_select === group.max_select ? `${group.min_select}` : `${group.min_select}–${group.max_select}`})</span>
                </p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {activeChoices.map((choice) => {
                    const sel = chosen.find((c) => c.choice_id === choice.choice_id);
                    return (
                      <div
                        key={choice.choice_id}
                        className={`p-3 rounded-lg border ${sel ? 'border-[#C86B51] bg-[#C86B51]/5' : 'border-[#E3DED5]'}`}
                        data-testid={`choice-${option.key}-${choice.name}`}
                      >
                        <div className="flex items-center justify-between">
                          <button type="button" onClick={() => toggleChoice(choice)} className="text-left flex-1">
                            <span className="text-sm text-[#2A312C]">{choice.name}</span>
                            {choice.yield_per_batch != null && (
                              <span className="block text-xs text-[#5C6A61]">{choice.yield_per_batch} {choice.yield_unit || 'pots'} / lot</span>
                            )}
                          </button>
                          {isBatchMode && sel ? (
                            <div className="flex items-center gap-2">
                              <button type="button" onClick={() => setBatches(choice, -1)} className="w-6 h-6 rounded border border-[#E3DED5]">−</button>
                              <span className="text-sm w-4 text-center">{sel.batches}</span>
                              <button type="button" onClick={() => setBatches(choice, +1)} className="w-6 h-6 rounded border border-[#E3DED5]">+</button>
                            </div>
                          ) : (
                            <Checkbox checked={!!sel} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MenusStep({ formData, setFormData, getRecipesByCategory }) {
  const selectRecipe = (bloc, recipeId) => {
    setFormData({
      ...formData,
      selected_recipes: { ...formData.selected_recipes, [bloc]: recipeId }
    });
  };

  const menuBlocs = [
    { 
      title: 'Bloc 1 : Plats principaux',
      description: 'Choisissez un mijoté, un plat au four et un poisson',
      items: [
        { key: 'bloc1_mijote', label: 'Mijoté', category: 'mijote' },
        { key: 'bloc1_four', label: 'Plat au four', category: 'four' },
        { key: 'bloc1_poisson', label: 'Poisson', category: 'poisson' }
      ]
    },
    {
      title: 'Bloc 2 : Soupes',
      description: 'Sélectionnez 2 soupes (les légumes sont réutilisés)',
      items: [
        { key: 'bloc2_soupe1', label: 'Soupe 1', category: 'soupe' },
        { key: 'bloc2_soupe2', label: 'Soupe 2', category: 'soupe' }
      ]
    },
    {
      title: 'Bloc 3 : Tartes & Pizzas',
      description: 'Choisissez 2 tartes ou pizzas',
      items: [
        { key: 'bloc3_tarte1', label: 'Tarte/Pizza 1', category: 'tarte_pizza' },
        { key: 'bloc3_tarte2', label: 'Tarte/Pizza 2', category: 'tarte_pizza' }
      ]
    },
    {
      title: 'Bloc 4 : Sauce',
      description: 'Une sauce maison pour accompagner vos pâtes',
      items: [
        { key: 'bloc4_sauce', label: 'Sauce', category: 'sauce' }
      ]
    },
    {
      title: 'Bloc 5 : Accompagnements (automatique)',
      description: 'Pâtes et gratin inclus dans votre formule',
      items: [
        { key: 'bloc5_pates', label: 'Pâtes', category: 'pates', optional: true },
        { key: 'bloc5_gratin', label: 'Gratin', category: 'gratin', optional: true }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Composez vos 10 repas
        </h2>
        <p className="text-[#5C6A61]">Sélectionnez vos plats parmi notre carte de saison</p>
      </div>
      
      {menuBlocs.map((bloc, blocIndex) => (
        <div key={blocIndex} className="space-y-4">
          <div>
            <h3 className="font-semibold text-[#2A312C]">{bloc.title}</h3>
            <p className="text-sm text-[#5C6A61]">{bloc.description}</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {bloc.items.map((item) => {
              const categoryRecipes = getRecipesByCategory(item.category);
              const selectedRecipe = formData.selected_recipes[item.key];
              
              return (
                <div key={item.key}>
                  <Label className="text-[#5C6A61] text-sm mb-2 block">
                    {item.label} {item.optional ? '(optionnel)' : '*'}
                  </Label>
                  <select
                    value={selectedRecipe || ''}
                    onChange={(e) => selectRecipe(item.key, e.target.value || null)}
                    className="input-default"
                    data-testid={`select-${item.key}`}
                  >
                    <option value="">Choisir...</option>
                    {categoryRecipes.map((recipe) => (
                      <option key={recipe.recipe_id} value={recipe.recipe_id}>
                        {recipe.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// New Categorized Menus Step - Uses dynamic categories from admin
function MenusStepCategorized({ formData, setFormData, menus, selectedMenu, setSelectedMenu, recipes, getRecipesByCategory, activeSeason, seasonError, loadingMenus }) {
  
  const toggleRecipeSelection = (categoryId, recipeId, maxChoices) => {
    const currentSelections = formData.category_selections[categoryId] || [];
    
    if (currentSelections.includes(recipeId)) {
      // Deselect
      const newSelections = currentSelections.filter(id => id !== recipeId);
      setFormData({
        ...formData,
        category_selections: {
          ...formData.category_selections,
          [categoryId]: newSelections
        }
      });
    } else {
      // Select - check max
      if (currentSelections.length >= maxChoices) {
        // Replace oldest selection
        const newSelections = [...currentSelections.slice(1), recipeId];
        setFormData({
          ...formData,
          category_selections: {
            ...formData.category_selections,
            [categoryId]: newSelections
          }
        });
      } else {
        setFormData({
          ...formData,
          category_selections: {
            ...formData.category_selections,
            [categoryId]: [...currentSelections, recipeId]
          }
        });
      }
    }
  };

  const handleMenuChange = (menu) => {
    setSelectedMenu(menu);
    setFormData({
      ...formData,
      menu_id: menu.menu_id,
      category_selections: {}, // Reset selections when changing menu
      no_fish: false,
      fish_replacement: null,
    });
  };

  const fishCategories = (selectedMenu?.categories || []).filter(c => c.category_type === 'fish');
  const hasFish = fishCategories.length > 0;
  const substitutionCategories = (selectedMenu?.categories || []).filter(c => ['simmered', 'oven'].includes(c.category_type));
  const replacementOptions = substitutionCategories.flatMap(c =>
    (c.recipes || [])
      .filter(r => !(formData.category_selections[c.category_id] || []).includes(r.recipe_id))
      .map(r => ({ ...r, category_id: c.category_id, category_name: c.name }))
  );

  const toggleNoFish = (checked) => {
    const newCS = { ...formData.category_selections };
    if (checked) {
      fishCategories.forEach(c => { newCS[c.category_id] = []; });
    }
    setFormData({
      ...formData,
      no_fish: checked,
      fish_replacement: checked ? formData.fish_replacement : null,
      category_selections: newCS,
    });
  };

  const selectReplacement = (recipe) => {
    const cur = formData.fish_replacement;
    const next = (cur && cur.recipe_id === recipe.recipe_id)
      ? null
      : { recipe_id: recipe.recipe_id, category_id: recipe.category_id };
    setFormData({ ...formData, fish_replacement: next });
  };

  const getRecipeById = (recipeId) => {
    return recipes.find(r => r.recipe_id === recipeId);
  };

  // Show loading state
  if (loadingMenus) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#A5BBA8] mb-4" />
          <p className="text-[#5C6A61]">Chargement des menus de saison...</p>
        </div>
      </div>
    );
  }

  // Show season error
  if (seasonError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="font-semibold text-red-800 text-lg mb-2">
            Réservation impossible pour cette date
          </h3>
          <p className="text-red-700">{seasonError}</p>
          <p className="text-sm text-red-600 mt-4">
            Veuillez sélectionner une autre date de prestation ou contacter notre équipe.
          </p>
        </div>
      </div>
    );
  }

  // Aucun menu disponible pour la saison/date : message clair côté client
  if (!menus || menus.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-[#F4F1EA] border border-[#E3DED5] rounded-xl p-6 text-center" data-testid="no-menu-available">
          <Utensils className="w-12 h-12 text-[#A5BBA8] mx-auto mb-4" />
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
            Composez vos repas
          </h3>
          <p className="text-[#5C6A61]">
            Aucun menu n'est actuellement disponible pour cette période. Merci de réessayer
            ultérieurement ou de choisir une autre date.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Composez vos 10 repas
        </h2>
        {activeSeason ? (
          <div className="flex items-center gap-2 text-[#5C6A61]">
            <span>Carte de saison :</span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#A5BBA8]/20 text-[#2A312C] font-medium">
              <Utensils className="w-4 h-4" />
              {activeSeason.name}
            </span>
          </div>
        ) : (
          <p className="text-[#5C6A61]">Sélectionnez vos plats parmi notre carte</p>
        )}
      </div>

      {/* Menu selection if multiple */}
      {menus.length > 1 && (
        <div className="space-y-3">
          <Label className="text-base font-semibold">Choisir un menu</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {menus.map(menu => (
              <div
                key={menu.menu_id}
                onClick={() => handleMenuChange(menu)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                  selectedMenu?.menu_id === menu.menu_id
                    ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                    : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#2A312C]">{menu.name}</h3>
                  {selectedMenu?.menu_id === menu.menu_id && (
                    <Check className="w-5 h-5 text-[#A5BBA8]" />
                  )}
                </div>
                {menu.description && (
                  <p className="text-sm text-[#5C6A61] mt-1">{menu.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fish opt-out */}
      {hasFish && (
        <div className="p-4 rounded-xl border-2 border-[#E3DED5] bg-white" data-testid="no-fish-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={formData.no_fish}
              onCheckedChange={(checked) => toggleNoFish(!!checked)}
              data-testid="no-fish-checkbox"
            />
            <span>
              <span className="font-medium text-[#2A312C]">Je ne souhaite pas de poisson</span>
              <span className="block text-sm text-[#5C6A61] mt-0.5">
                Le poisson sera remplacé par un plat mijoté ou au four supplémentaire. Le nombre total de plats reste identique.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Categories from selected menu */}
      {selectedMenu && selectedMenu.categories && selectedMenu.categories.length > 0 ? (
        <div className="space-y-8">
          {selectedMenu.categories.map((category, idx) => {
            if (formData.no_fish && category.category_type === 'fish') return null;
            const selections = formData.category_selections[category.category_id] || [];
            const categoryRecipes = category.recipes || [];
            
            return (
              <div key={category.category_id || idx} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-[#2A312C] text-lg">{category.name}</h3>
                    <p className="text-sm text-[#5C6A61]">
                      Choisissez {category.max_choices} plat(s) parmi {categoryRecipes.length} options
                    </p>
                  </div>
                  <div className="text-sm">
                    <span className={`font-semibold ${selections.length === category.max_choices ? 'text-[#A5BBA8]' : 'text-[#C86B51]'}`}>
                      {selections.length}/{category.max_choices}
                    </span>
                  </div>
                </div>
                
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categoryRecipes.map(recipe => {
                    const isSelected = selections.includes(recipe.recipe_id);
                    return (
                      <div
                        key={recipe.recipe_id}
                        onClick={() => toggleRecipeSelection(category.category_id, recipe.recipe_id, category.max_choices)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#A5BBA8] bg-[#A5BBA8]/10 shadow-sm'
                            : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                        }`}
                        data-testid={`recipe-${recipe.recipe_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-[#2A312C]">{recipe.name}</h4>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected 
                              ? 'bg-[#A5BBA8] border-[#A5BBA8]' 
                              : 'border-[#E3DED5]'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                        {recipe.description && (
                          <p className="text-xs text-[#5C6A61] mt-1 line-clamp-2">{recipe.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-6 bg-[#F9F6F0] rounded-xl text-center">
          <Utensils className="w-12 h-12 text-[#E3DED5] mx-auto mb-3" />
          <p className="text-[#5C6A61]">Ce menu n'a pas encore de catégories configurées.</p>
        </div>
      )}

      {/* Fish replacement selector */}
      {formData.no_fish && hasFish && (
        <div className="space-y-4" data-testid="fish-replacement-block">
          <div>
            <h3 className="font-semibold text-[#2A312C] text-lg">Remplacement du poisson</h3>
            <p className="text-sm text-[#5C6A61]">
              Choisissez 1 plat mijoté ou au four supplémentaire pour remplacer le poisson.
            </p>
          </div>
          {replacementOptions.length === 0 ? (
            <p className="text-sm text-[#C86B51]">Aucun plat de remplacement disponible pour ce menu.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {replacementOptions.map(recipe => {
                const isSelected = formData.fish_replacement?.recipe_id === recipe.recipe_id;
                return (
                  <div
                    key={recipe.recipe_id}
                    onClick={() => selectReplacement(recipe)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#A5BBA8] bg-[#A5BBA8]/10 shadow-sm'
                        : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                    }`}
                    data-testid={`fish-replacement-${recipe.recipe_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium text-[#2A312C]">{recipe.name}</h4>
                        <p className="text-xs text-[#5C6A61] mt-0.5">{recipe.category_name}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-[#A5BBA8] border-[#A5BBA8]' : 'border-[#E3DED5]'
                      }`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {selectedMenu && Object.keys(formData.category_selections).length > 0 && (
        <div className="p-4 bg-[#A5BBA8]/10 rounded-xl">
          <h4 className="font-semibold text-[#2A312C] mb-2">Vos choix</h4>
          <div className="space-y-1">
            {Object.entries(formData.category_selections).map(([catId, recipeIds]) => {
              const category = selectedMenu.categories.find(c => c.category_id === catId);
              if (formData.no_fish && category?.category_type === 'fish') return null;
              if (!recipeIds || recipeIds.length === 0) return null;
              return (
                <div key={catId} className="text-sm text-[#5C6A61]">
                  <span className="font-medium">{category?.name}:</span>{' '}
                  {recipeIds.map(rid => {
                    const recipe = category?.recipes?.find(r => r.recipe_id === rid);
                    return recipe?.name || rid;
                  }).join(', ')}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CreneauStep({ formData, setFormData, availableSlots, sectorCheck, formulaKey }) {
  const { user } = useAuth();
  const [showWaitingForm, setShowWaitingForm] = useState(false);
  const [waitingFormData, setWaitingFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    city: formData.city || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [smartSlots, setSmartSlots] = useState([]);
  const [loadingSmart, setLoadingSmart] = useState(false);
  const [smartReason, setSmartReason] = useState(null);
  const [alertSent, setAlertSent] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  const optionsKey = JSON.stringify(formData.options || {});
  useEffect(() => {
    if (!formData.date || !sectorCheck?.in_sector || !formulaKey) { setSmartSlots([]); setSmartReason(null); return; }
    const dateStr = toLocalDateStr(formData.date);
    const opts = Object.entries(formData.options || {}).filter(([, v]) => v).map(([k]) => k).join(',');
    setLoadingSmart(true);
    setAlertSent(false);
    clientApi.getSmartSlots({
      postal_code: formData.postal_code, date: dateStr, formula_key: formulaKey, options: opts,
      household_size: formData.household_size, city: formData.city || undefined,
    })
      .then((res) => { setSmartSlots(res.data.slots || []); setSmartReason(res.data.reason_label || null); })
      .catch(() => { setSmartSlots([]); setSmartReason(null); })
      .finally(() => setLoadingSmart(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date, formData.postal_code, formData.household_size, optionsKey, sectorCheck?.in_sector, formulaKey]);

  const requestAlert = async () => {
    if (!formData.date) return;
    const dateStr = toLocalDateStr(formData.date);
    const opts = Object.entries(formData.options || {}).filter(([, v]) => v).map(([k]) => k);
    setSendingAlert(true);
    try {
      await clientApi.requestSlotAlert({
        postal_code: formData.postal_code, date: dateStr, formula: formulaKey,
        household_size: formData.household_size, options: opts,
      });
      setAlertSent(true);
      toast.success('Nous vous préviendrons si un créneau correspondant se libère.');
    } catch (e) {
      toast.error("Impossible d'enregistrer votre demande d'alerte pour le moment.");
    } finally {
      setSendingAlert(false);
    }
  };

  const fmtDuration = (min) => {
    const h = Math.floor(min / 60), m = min % 60;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
  };
  const selectSmartSlot = (s) => {
    setFormData({
      ...formData,
      slot_type: parseInt(s.start_time.split(':')[0], 10) < 12 ? 'matin' : 'apres_midi',
      preferred_time: s.start_time,
      slot_id: `${s.employee_id}_${s.start_time}`,
      employee_id: s.employee_id,
      start_time: s.start_time,
      end_time: s.end_time,
    });
    track('select_slot', { start_time: s.start_time });
  };

  const handleWaitingListSubmit = async () => {
    if (!waitingFormData.email && !waitingFormData.phone) {
      toast.error('Veuillez renseigner un email ou un téléphone');
      return;
    }
    
    setSubmitting(true);
    try {
      await clientApi.registerWaitingList({
        postal_code: formData.postal_code,
        city: waitingFormData.city,
        email: waitingFormData.email,
        phone: waitingFormData.phone,
        name: waitingFormData.name
      });
      setSubmitted(true);
      toast.success('Merci ! Nous vous contacterons dès que votre secteur sera disponible.');
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Choisissez votre créneau
        </h2>
        <p className="text-[#5C6A61]">Sélectionnez la date et l'heure de l'intervention</p>
      </div>
      
      {!sectorCheck?.in_sector && formData.postal_code?.length === 5 && (
        <div className="p-6 bg-[#E8B25C]/10 rounded-xl border border-[#E8B25C]/30">
          <p className="text-[#E8B25C] font-semibold text-lg mb-2">Secteur pas encore disponible</p>
          <p className="text-[#5C6A61] mb-4">
            Votre code postal ({formData.postal_code}) n'est pas encore dans notre zone d'intervention. 
            Souhaitez-vous être recontacté lors de l'ouverture de votre secteur ?
          </p>
          
          {!submitted ? (
            <>
              {!showWaitingForm ? (
                <button 
                  onClick={() => setShowWaitingForm(true)}
                  className="btn-secondary"
                  data-testid="show-waiting-form-btn"
                >
                  Oui, me prévenir !
                </button>
              ) : (
                <div className="space-y-4 bg-white p-4 rounded-lg mt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Votre nom</Label>
                      <input
                        type="text"
                        value={waitingFormData.name}
                        onChange={(e) => setWaitingFormData({...waitingFormData, name: e.target.value})}
                        className="input-default"
                        placeholder="Jean Dupont"
                        data-testid="waiting-name-input"
                      />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <input
                        type="text"
                        value={waitingFormData.city}
                        onChange={(e) => setWaitingFormData({...waitingFormData, city: e.target.value})}
                        className="input-default"
                        placeholder="Votre ville"
                        data-testid="waiting-city-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <input
                        type="email"
                        value={waitingFormData.email}
                        onChange={(e) => setWaitingFormData({...waitingFormData, email: e.target.value})}
                        className="input-default"
                        placeholder="votre@email.fr"
                        data-testid="waiting-email-input"
                      />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <input
                        type="tel"
                        value={waitingFormData.phone}
                        onChange={(e) => setWaitingFormData({...waitingFormData, phone: e.target.value})}
                        className="input-default"
                        placeholder="06 12 34 56 78"
                        data-testid="waiting-phone-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleWaitingListSubmit}
                      disabled={submitting}
                      className="btn-primary"
                      data-testid="submit-waiting-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                      M'inscrire à la liste d'attente
                    </button>
                    <button 
                      onClick={() => setShowWaitingForm(false)}
                      className="btn-outline"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
              <Check className="w-5 h-5" />
              <span>Vous êtes inscrit(e) ! Nous vous contacterons dès que votre secteur sera disponible.</span>
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-center">
        <CalendarComponent
          mode="single"
          locale={fr}
          weekStartsOn={1}
          formatters={{ formatWeekdayName: (day) => ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][day.getDay()] }}
          selected={formData.date}
          onSelect={(date) => setFormData({ ...formData, date, slot_id: null })}
          disabled={(date) => {
            const now = new Date();
            const min48 = new Date(now.getTime() + 48 * 3600 * 1000);
            const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
            return date < now || date.getDay() === 0 || endOfDay < min48;
          }}
          className="rounded-xl border border-[#E3DED5]"
          data-testid="calendar"
        />
      </div>
      
      {formData.date && sectorCheck?.in_sector && (
        <div className="space-y-6">
          {/* Créneaux recommandés (moteur intelligent) */}
          <div>
            <Label className="text-[#2A312C] mb-2 block font-semibold">
              Créneaux recommandés le {formatDate(formData.date)}
            </Label>
            <p className="text-sm text-[#5C6A61] mb-3">
              Nous vous proposons uniquement les meilleurs horaires disponibles pour ce jour.
            </p>
            {loadingSmart ? (
              <div className="flex items-center gap-2 text-[#5C6A61] py-4" data-testid="smart-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> Recherche des meilleurs créneaux...
              </div>
            ) : smartSlots.length === 0 ? (
              <div className="p-4 bg-[#F9F6F0] rounded-lg space-y-3" data-testid="smart-no-slots">
                <p className="text-[#5C6A61]">
                  Aucun créneau disponible ce jour-là. Merci de choisir une autre date ou une autre période.
                </p>
                {alertSent ? (
                  <div className="flex items-center gap-2 text-[#5C7C61] text-sm" data-testid="alert-optin-sent">
                    <Check className="w-4 h-4" /> Nous vous préviendrons si un créneau correspondant se libère.
                  </div>
                ) : (
                  <div className="pt-1 border-t border-[#E3DED5]">
                    <p className="text-sm text-[#2A312C] mt-2 mb-2">
                      Souhaitez-vous être informé si un créneau correspondant devient disponible ?
                    </p>
                    <button
                      type="button"
                      onClick={requestAlert}
                      disabled={sendingAlert}
                      className="btn-secondary text-sm disabled:opacity-50"
                      data-testid="alert-optin-btn"
                    >
                      {sendingAlert ? 'Enregistrement...' : "M'informer si un créneau se libère"}
                    </button>
                    <p className="text-xs text-[#5C6A61] mt-2 opacity-80">
                      Cette alerte est informative : elle ne réserve aucun créneau et ne donne aucune priorité.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3" data-testid="smart-slots">
                {smartSlots.map((s, i) => {
                  const selected = formData.slot_id === `${s.employee_id}_${s.start_time}`;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectSmartSlot(s)}
                      data-testid={`smart-slot-${i}`}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        selected ? 'border-[#A5BBA8] bg-[#A5BBA8]/10 shadow-sm' : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                      }`}
                    >
                      {i === 0 && (
                        <span className="absolute top-2 right-2 text-[10px] font-semibold uppercase tracking-wide bg-[#A5BBA8] text-white px-2 py-0.5 rounded-full">
                          Recommandé
                        </span>
                      )}
                      <div className="text-lg font-semibold text-[#2A312C]">{s.start_time} – {s.end_time}</div>
                      <div className="text-xs text-[#5C6A61] mt-1">Durée {fmtDuration(s.duration_minutes)}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Choix manuel legacy Matin/Après-midi RETIRÉ du rendu : le moteur intelligent
              (créneaux calculés selon l'amplitude admin) est désormais l'unique source. */}
          {false && (
            <div className="mt-4 space-y-6">
          {/* Slot Type Selection */}
          <div>
            <Label className="text-[#2A312C] mb-3 block font-semibold">
              Choisissez votre créneau le {formatDate(formData.date)}
            </Label>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, slot_type: 'matin', preferred_time: '', employee_id: null, start_time: null, end_time: null, slot_id: null })}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  formData.slot_type === 'matin'
                    ? 'border-[#A5BBA8] bg-[#A5BBA8]/10 shadow-sm'
                    : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                }`}
                data-testid="slot-matin"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold text-[#2A312C]">Matin</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    formData.slot_type === 'matin' ? 'bg-[#A5BBA8] border-[#A5BBA8]' : 'border-[#E3DED5]'
                  }`}>
                    {formData.slot_type === 'matin' && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
                <p className="text-[#5C6A61]">08h00 - 12h00</p>
                <p className="text-xs text-[#5C6A61] mt-1">4 heures d'intervention</p>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData({ ...formData, slot_type: 'apres_midi', preferred_time: '', employee_id: null, start_time: null, end_time: null, slot_id: null })}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  formData.slot_type === 'apres_midi'
                    ? 'border-[#A5BBA8] bg-[#A5BBA8]/10 shadow-sm'
                    : 'border-[#E3DED5] hover:border-[#A5BBA8]'
                }`}
                data-testid="slot-apres-midi"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold text-[#2A312C]">Après-midi</span>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    formData.slot_type === 'apres_midi' ? 'bg-[#A5BBA8] border-[#A5BBA8]' : 'border-[#E3DED5]'
                  }`}>
                    {formData.slot_type === 'apres_midi' && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
                <p className="text-[#5C6A61]">13h30 - 17h30</p>
                <p className="text-xs text-[#5C6A61] mt-1">4 heures d'intervention</p>
              </button>
            </div>
          </div>

          {/* Preferred Time (optional) */}
          <div>
            <Label className="text-[#2A312C] mb-2 block">
              Préférence horaire <span className="text-[#5C6A61] font-normal">(indicatif)</span>
            </Label>
            <p className="text-sm text-[#5C6A61] mb-3">
              Indiquez votre heure préférée à l'intérieur du créneau. Cette information est indicative et nous aiderons à organiser l'intervention au mieux.
            </p>
            <select
              value={formData.preferred_time}
              onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
              className="input-default max-w-xs"
              data-testid="preferred-time-select"
            >
              <option value="">Pas de préférence</option>
              {formData.slot_type === 'matin' ? (
                <>
                  <option value="08:00">08h00</option>
                  <option value="08:30">08h30</option>
                  <option value="09:00">09h00</option>
                  <option value="09:30">09h30</option>
                  <option value="10:00">10h00</option>
                </>
              ) : (
                <>
                  <option value="13:30">13h30</option>
                  <option value="14:00">14h00</option>
                  <option value="14:30">14h30</option>
                  <option value="15:00">15h00</option>
                  <option value="15:30">15h30</option>
                </>
              )}
            </select>
          </div>
            </div>
          )}

          {/* Drive info notice */}
          {formData.options?.drive && (
            <div className="p-4 bg-[#A5BBA8]/10 rounded-xl border border-[#A5BBA8]/30">
              <p className="text-sm text-[#2A312C] font-medium mb-1">Option Drive activée</p>
              <p className="text-sm text-[#5C6A61]">
                Vous pourrez renseigner les informations du drive (magasin, horaire, code) après votre réservation, 
                une fois la liste de courses reçue. Le temps de récupération est inclus dans les 4 heures d'intervention.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EquipementsStep({ formData, setFormData }) {
  const toggleEquipment = (key) => {
    setFormData({
      ...formData,
      equipment: { ...formData.equipment, [key]: !formData.equipment[key] }
    });
  };

  const requiredEquipment = [
    { key: 'four', label: 'Four', required: true },
    { key: 'plaques', label: 'Plaques de cuisson', required: true },
    { key: 'frigo', label: 'Réfrigérateur', required: true },
    { key: 'evier', label: 'Évier', required: true }
  ];

  const optionalEquipment = [
    { key: 'mixeur', label: 'Mixeur / Blender' },
    { key: 'casseroles', label: 'Casseroles (diverses tailles)' },
    { key: 'poele', label: 'Poêle' },
    { key: 'plat', label: 'Plat allant au four' },
    { key: 'faitout', label: 'Faitout / Cocotte' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Équipements disponibles
        </h2>
        <p className="text-[#5C6A61]">Confirmez les équipements présents dans votre cuisine</p>
      </div>
      
      <div>
        <h3 className="font-semibold text-[#2A312C] mb-3">Équipements obligatoires</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {requiredEquipment.map((item) => (
            <div
              key={item.key}
              onClick={() => toggleEquipment(item.key)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-colors flex items-center gap-3 ${
                formData.equipment[item.key]
                  ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                  : 'border-[#C86B51] bg-[#C86B51]/5'
              }`}
              data-testid={`equipment-${item.key}`}
            >
              <Checkbox checked={formData.equipment[item.key]} />
              <span className="text-[#2A312C]">{item.label}</span>
              <span className="text-xs text-[#C86B51] ml-auto">Requis</span>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className="font-semibold text-[#2A312C] mb-3">Équipements recommandés</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {optionalEquipment.map((item) => (
            <div
              key={item.key}
              onClick={() => toggleEquipment(item.key)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-colors flex items-center gap-3 ${
                formData.equipment[item.key]
                  ? 'border-[#A5BBA8] bg-[#A5BBA8]/10'
                  : 'border-[#E3DED5] hover:border-[#A5BBA8]'
              }`}
              data-testid={`equipment-${item.key}`}
            >
              <Checkbox checked={formData.equipment[item.key]} />
              <span className="text-[#2A312C]">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RecapStep({ formData, setFormData, formula, formulaInfo, calculateTotal, recipes, optionsMap, selectedMenu, sectorCheck, promo }) {
  const { config } = useConfig();
  const getRecipeName = (recipeId) => {
    const recipe = recipes.find(r => r.recipe_id === recipeId);
    return recipe?.name || '-';
  };

  // Avance immédiate (administrable)
  const avanceEnabled = config?.sap_avance_immediate_enabled ?? false;
  const avanceRate = config?.sap_avance_immediate_rate ?? 50;
  const avanceLabel = config?.sap_avance_immediate_text || 'Avance immédiate';

  const total = calculateTotal();
  const promoPercent = promo?.eligible ? Number(promo.percent || 0) : 0;
  const promoDiscount = Math.round(total * promoPercent) / 100;
  const amountToPay = total - promoDiscount;
  const estimatedUrssafCredit = (avanceEnabled && formData.urssaf_advance_enabled) ? amountToPay * (avanceRate / 100) : 0;

  // Fallback options if optionsMap is empty
  const getOptionInfo = (key) => {
    if (optionsMap[key]) return optionsMap[key];
    const defaults = {
      lunch: { name: 'Lunch', price: 30 },
      dessert: { name: 'Dessert', price: 25 },
      patisserie: { name: 'Pâtisserie', price: 35 },
      drive: { name: 'Drive', price: 20 }
    };
    return defaults[key] || { name: key, price: 0 };
  };

  // Get all selected recipes from category_selections
  const getSelectedRecipes = () => {
    if (selectedMenu && Object.keys(formData.category_selections).length > 0) {
      const allRecipes = [];
      selectedMenu.categories?.forEach(cat => {
        if (formData.no_fish && cat.category_type === 'fish') return;
        const selections = formData.category_selections[cat.category_id] || [];
        selections.forEach(recipeId => {
          const recipe = cat.recipes?.find(r => r.recipe_id === recipeId);
          if (recipe) {
            allRecipes.push({ category: cat.name, name: recipe.name });
          }
        });
      });
      // Fish replacement dish
      if (formData.no_fish && formData.fish_replacement) {
        const cat = selectedMenu.categories?.find(c => c.category_id === formData.fish_replacement.category_id);
        const recipe = cat?.recipes?.find(r => r.recipe_id === formData.fish_replacement.recipe_id);
        if (recipe) {
          allRecipes.push({ category: `${cat.name} (remplace le poisson)`, name: recipe.name });
        }
      }
      return allRecipes;
    }
    // Legacy fallback
    return Object.entries(formData.selected_recipes)
      .filter(([, recipeId]) => recipeId)
      .map(([, recipeId]) => ({ category: '', name: getRecipeName(recipeId) }));
  };

  const selectedRecipesList = getSelectedRecipes();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
          Récapitulatif de votre réservation
        </h2>
        <p className="text-[#5C6A61]">Vérifiez les détails avant de confirmer</p>
      </div>
      
      {/* Foyer & Date & Créneau */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-4 bg-[#F9F6F0] rounded-xl">
          <p className="text-sm text-[#5C6A61] mb-1">Foyer</p>
          <p className="font-semibold text-[#2A312C]">{formData.household_size} personne(s) - {formulaInfo.name}</p>
        </div>
        <div className="p-4 bg-[#F9F6F0] rounded-xl">
          <p className="text-sm text-[#5C6A61] mb-1">Date</p>
          <p className="font-semibold text-[#2A312C]">{formData.date ? formatDate(formData.date) : 'Non sélectionnée'}</p>
        </div>
        <div className="p-4 bg-[#F9F6F0] rounded-xl">
          <p className="text-sm text-[#5C6A61] mb-1">Créneau</p>
          <p className="font-semibold text-[#2A312C]" data-testid="recap-creneau">
            {formData.start_time && formData.end_time ? `${formData.start_time} – ${formData.end_time}` : 'Non sélectionné'}
          </p>
        </div>
      </div>
      
      {/* Menus - New categorized display */}
      <div>
        <h3 className="font-semibold text-[#2A312C] mb-3">
          Vos repas {selectedMenu && <span className="font-normal text-[#5C6A61]">({selectedMenu.name})</span>}
        </h3>
        {formData.no_fish && (
          <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C86B51]/10 text-[#C86B51] text-sm font-medium" data-testid="recap-no-fish">
            <Check className="w-4 h-4" />
            Sans poisson — remplacé par un plat mijoté ou au four
          </div>
        )}
        {selectedRecipesList.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {selectedRecipesList.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-[#A5BBA8]" />
                <span className="text-[#2A312C]">
                  {item.category && <span className="text-[#5C6A61]">{item.category}: </span>}
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#5C6A61]">Aucun plat sélectionné</p>
        )}
      </div>
      
      {/* Options */}
      {Object.values(formData.options).some(v => v) && (
        <div>
          <h3 className="font-semibold text-[#2A312C] mb-3">Options</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(formData.options).map(([key, value]) => {
              const opt = getOptionInfo(key);
              return value && (
                <span key={key} className="px-3 py-1 bg-[#C86B51]/10 text-[#C86B51] rounded-full text-sm">
                  {opt.name} (+{formatPrice(opt.price)})
                </span>
              );
            })}
          </div>
        </div>
      )}
      
      {/* URSSAF / Avance immédiate — visible seulement si activée par l'admin */}
      {avanceEnabled && (
      <div className="p-4 border border-[#E3DED5] rounded-xl space-y-3" data-testid="urssaf-advance-section">
        <h3 className="font-semibold text-[#2A312C]">{avanceLabel}</h3>
        <p className="text-sm text-[#5C6A61]">
          Si vous êtes éligible au crédit d'impôt services à la personne, vous pouvez bénéficier de l'avance immédiate.
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.has_urssaf_account}
              onCheckedChange={(checked) => setFormData({ ...formData, has_urssaf_account: checked })}
              id="urssaf-account"
            />
            <Label htmlFor="urssaf-account" className="text-sm">J'ai un compte URSSAF services à la personne</Label>
          </div>
          {formData.has_urssaf_account && (
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={formData.urssaf_advance_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, urssaf_advance_enabled: checked })}
                id="urssaf-advance"
              />
              <Label htmlFor="urssaf-advance" className="text-sm">Activer l'avance immédiate</Label>
            </div>
          )}
          <p className="text-xs text-[#5C6A61] opacity-80">
            L'avantage dépend de votre éligibilité et de l'activation du service auprès de l'URSSAF.
          </p>
        </div>
      </div>
      )}
      
      {/* Notes */}
      <div>
        <Label className="text-[#2A312C] mb-2 block">Notes particulières (optionnel)</Label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          className="input-default min-h-[100px]"
          placeholder="Allergies, régimes alimentaires, préférences..."
          data-testid="notes-input"
        />
      </div>
      
      {/* Cancellation Policy */}
      <div className="p-4 border border-[#E8B25C]/40 bg-[#E8B25C]/5 rounded-xl" data-testid="cancellation-policy">
        <h3 className="font-semibold text-[#2A312C] mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#E8B25C]" />
          Politique d'annulation
        </h3>
        <p className="text-sm text-[#5C6A61]">
          Annulation gratuite jusqu'à <strong>7 jours</strong> avant l'intervention.
        </p>
        <p className="text-sm text-[#5C6A61] mt-1">
          Passé ce délai, la prestation reste due et aucun remboursement ne sera effectué.
        </p>
      </div>
      
      {/* Total */}
      <div className="p-6 bg-[#2A312C] text-white rounded-xl">
        <div className="flex justify-between items-center mb-2">
          <span>Formule {formulaInfo.name}</span>
          <span>{formatPrice(formulaInfo.price)}</span>
        </div>
        {Object.entries(formData.options).map(([key, value]) => {
          const opt = getOptionInfo(key);
          return value && (
            <div key={key} className="flex justify-between items-center mb-2 text-sm opacity-80">
              <span>{opt.name}</span>
              <span>+{formatPrice(opt.price)}</span>
            </div>
          );
        })}
        {Number(sectorCheck?.sector?.travel_surcharge || 0) > 0 && (
          <div className="flex justify-between items-center mb-2 text-sm opacity-80" data-testid="recap-travel-surcharge">
            <span>Supplément déplacement — {sectorCheck.sector.name}</span>
            <span>+{formatPrice(sectorCheck.sector.travel_surcharge)}</span>
          </div>
        )}
        <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-center text-xl font-semibold">
          <span>{promoPercent > 0 ? 'Tarif initial' : 'Total'}</span>
          <span data-testid="recap-total">{formatPrice(total)}</span>
        </div>
        {promoPercent > 0 && (
          <div className="mt-2" data-testid="recap-promo">
            <div className="flex justify-between items-center text-[#E8B25C] text-sm font-medium">
              <span>{promo.label || 'Offre découverte'} {promo.code} (-{promoPercent} %)</span>
              <span data-testid="recap-promo-discount">-{formatPrice(promoDiscount)}</span>
            </div>
            <div className="flex justify-between items-center text-xl font-semibold mt-2">
              <span>Montant à payer</span>
              <span data-testid="recap-promo-final">{formatPrice(amountToPay)}</span>
            </div>
          </div>
        )}
        {avanceEnabled && formData.urssaf_advance_enabled && (
          <div className="mt-2 text-sm" data-testid="recap-avance-breakdown">
            <div className="flex justify-between items-center text-[#A5BBA8]">
              <span>Avance immédiate estimée ({avanceRate}%)</span>
              <span>-{formatPrice(estimatedUrssafCredit)}</span>
            </div>
            <div className="flex justify-between items-center font-semibold">
              <span>Reste à payer estimé</span>
              <span className="text-terracotta text-lg" data-testid="recap-reste-a-payer">{formatPrice(amountToPay - estimatedUrssafCredit)}</span>
            </div>
            <p className="text-xs opacity-60 mt-1">* Montant estimatif, sous réserve d'éligibilité et d'activation du service.</p>
          </div>
        )}
      </div>
    </div>
  );
}
